import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import FeedbackDialog from './FeedbackDialog';
import ToolsSwitcher from '../ToolsSwitcher/ToolsSwitcher';

type Shell = { openTools: () => void; openFeedback: () => void };

const ShellContext = createContext<Shell>({ openTools: () => {}, openFeedback: () => {} });

export function ShellProvider({ children }: { children: ReactNode }) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setToolsOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = useMemo(
    () => ({ openTools: () => setToolsOpen(true), openFeedback: () => setFeedbackOpen(true) }),
    []
  );

  return (
    <ShellContext.Provider value={value}>
      {children}
      <ToolsSwitcher
        open={toolsOpen}
        onOpenChange={setToolsOpen}
        onReportIssue={() => {
          setToolsOpen(false);
          setFeedbackOpen(true);
        }}
      />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </ShellContext.Provider>
  );
}

export const useShell = () => useContext(ShellContext);
